import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, BellRing, Bell, Share, Check, Download, Fingerprint, ScanFace, Trash2, Send, Loader2 } from "lucide-react";
import {
  isPwa, isIos, canInstall, onInstallAvailable, triggerInstall, requestNotifPermission,
  subscribeToPush, unsubscribeFromPush, getPushSubscription, sendTestPush,
} from "@/lib/pwa";
import axios from "axios";
import {
  isPasskeySupported, isPlatformAuthenticatorAvailable,
  registerPasskey, passkeyStatus, deletePasskey,
} from "@/lib/webauthn";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Card shown on the Admin Settings tab. Tells the owner how to install the
 * admin console as a mobile app, and lets them opt in to booking notifications.
 */
export const InstallCard = () => {
  const [installable, setInstallable] = useState(canInstall());
  const [installed] = useState(isPwa());

  useEffect(() => {
    const off = onInstallAvailable((v) => setInstallable(v));
    return off;
  }, []);

  const doInstall = async () => {
    const r = await triggerInstall();
    if (r?.outcome === "accepted") toast.success("Installed — check your home screen.");
    else if (r?.outcome === "dismissed") toast.info("You can install anytime from Settings.");
  };

  if (installed) {
    return (
      <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6" data-testid="pwa-install-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#B76E79] text-white flex items-center justify-center">
            <Check size={16} />
          </div>
          <div>
            <div className="text-sm text-[#14100F]">Installed on this device</div>
            <div className="text-xs text-[#6A5F58]">You're running the mobile admin app.</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6 space-y-4" data-testid="pwa-install-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] flex items-center justify-center">
          <Smartphone size={16} />
        </div>
        <div>
          <div className="text-sm text-[#14100F]">Install as a mobile app</div>
          <div className="text-xs text-[#6A5F58]">Home-screen icon, fullscreen, works offline for today's arrivals.</div>
        </div>
      </div>

      {installable && (
        <button
          type="button"
          onClick={doInstall}
          data-testid="pwa-install-btn"
          className="btn-primary font-brand text-xs tracking-[0.14em] uppercase inline-flex items-center gap-2"
        >
          <Download size={13} /> Install now
        </button>
      )}

      {!installable && isIos() && (
        <div className="text-sm text-[#6A5F58] bg-[#FAF5EC] border border-[#EDE3D2] rounded-md p-4 leading-relaxed">
          <div className="flex items-center gap-2 text-[#14100F] font-medium">
            <Share size={14} /> On iPhone or iPad:
          </div>
          <ol className="mt-2 list-decimal ml-5 space-y-1 text-xs">
            <li>Open this page in <strong>Safari</strong> (not Chrome).</li>
            <li>Tap the <Share size={11} className="inline mb-0.5" /> Share button at the bottom.</li>
            <li>Scroll and tap <strong>“Add to Home Screen”</strong>.</li>
            <li>Tap <strong>Add</strong>. WL Admin will appear like any other app.</li>
          </ol>
        </div>
      )}

      {!installable && !isIos() && (
        <div className="text-sm text-[#6A5F58] bg-[#FAF5EC] border border-[#EDE3D2] rounded-md p-4 leading-relaxed">
          <div className="flex items-center gap-2 text-[#14100F] font-medium">
            <Download size={14} /> On Android:
          </div>
          <p className="mt-2 text-xs">
            Open the admin site in <strong>Chrome</strong>, tap the ⋮ menu top-right, then <strong>“Add to Home screen”</strong> or <strong>“Install app”</strong>.
          </p>
        </div>
      )}
    </div>
  );
};

export const NotificationsCard = () => {
  const [permission, setPermission] = useState(() =>
    typeof Notification !== "undefined" ? Notification.permission : "unsupported",
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [briefTime, setBriefTime] = useState("07:00");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const sub = await getPushSubscription();
        setSubscribed(!!sub);
        // Read current brief time from server so this device uses the same
        const { data } = await axios.get(`${API_BASE}/api/admin/daily-brief/voice-settings`);
        if (data.voice_brief_time) setBriefTime(data.voice_brief_time);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (permission === "unsupported") return null;

  const subscribe = async () => {
    setBusy(true);
    try {
      await subscribeToPush({ apiBase: API_BASE, briefTime });
      setPermission(Notification.permission);
      setSubscribed(true);
      toast.success("Push turned on — you'll hear from us in the morning.");
    } catch (e) {
      toast.error(e?.message || "Couldn't turn push on.");
    } finally { setBusy(false); }
  };

  const unsubscribe = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush({ apiBase: API_BASE });
      setSubscribed(false);
      toast.success("Push turned off on this device.");
    } catch { toast.error("Couldn't unsubscribe."); }
    finally { setBusy(false); }
  };

  const testNow = async () => {
    setBusy(true);
    try {
      const r = await sendTestPush({ apiBase: API_BASE });
      toast.success(`Test sent to ${r.sent} device${r.sent === 1 ? "" : "s"}.`);
    } catch { toast.error("Test failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6 space-y-4" data-testid="pwa-notif-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] flex items-center justify-center">
          {subscribed ? <BellRing size={16} /> : <Bell size={16} />}
        </div>
        <div>
          <div className="text-sm text-[#14100F]">Morning push + booking alerts</div>
          <div className="text-xs text-[#6A5F58]">
            {subscribed
              ? `On — your device will get a push at ${briefTime} with the morning brief, plus alerts whenever a new booking lands.`
              : "Turn on to get a push notification with the morning brief at your chosen time (works even when the admin app is closed), plus new-booking alerts."}
          </div>
        </div>
      </div>

      {permission === "denied" && (
        <div className="text-xs text-[#B76E79]">
          Notifications are blocked in your browser or device settings. Open your settings and allow them for this site, then come back.
        </div>
      )}

      {isIos() && !window.matchMedia("(display-mode: standalone)").matches && (
        <div className="text-xs text-[#6A5F58] bg-[#FAF5EC] border border-[#EDE3D2] rounded-md p-3">
          <strong>On iPhone or iPad:</strong> push notifications only work once you've added this site to your Home Screen (Install as a mobile app above). Then open it from the home-screen icon and come back here.
        </div>
      )}

      {loading ? (
        <div className="text-xs text-[#6A5F58]">Checking…</div>
      ) : (
        <div className="flex flex-wrap gap-3">
          {!subscribed ? (
            <button
              type="button"
              onClick={subscribe}
              disabled={busy || permission === "denied"}
              data-testid="pwa-notif-enable"
              className="btn-primary font-brand text-xs tracking-[0.14em] uppercase inline-flex items-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <BellRing size={13} />} Turn on push
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={testNow}
                disabled={busy}
                data-testid="pwa-notif-test"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0F0E0E] text-[#0F0E0E] hover:bg-[#0F0E0E] hover:text-[#F5EFE6] transition font-brand text-xs tracking-[0.14em] uppercase disabled:opacity-50"
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send test push
              </button>
              <button
                type="button"
                onClick={unsubscribe}
                disabled={busy}
                className="inline-flex items-center gap-2 text-xs text-[#6A5F58] hover:text-[#B76E79] font-brand tracking-[0.14em] uppercase"
              >
                Turn off on this device
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------------
 * Passkey / Face ID card — one-time setup so the admin can sign back in
 * with a biometric on this device instead of typing the password.
 * ---------------------------------------------------------------------- */
export const PasskeyCard = () => {
  const [supported, setSupported] = useState(false);
  const [creds, setCreds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const s = await passkeyStatus({ apiBase: API_BASE });
      setCreds(s.credentials || []);
    } catch { /* not signed in yet */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      const ok = isPasskeySupported() && (await isPlatformAuthenticatorAvailable());
      setSupported(ok);
      await refresh();
    })();
  }, []);

  if (!supported) {
    return (
      <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6" data-testid="pwa-passkey-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#F5EFE6] text-[#6A5F58] flex items-center justify-center">
            <Fingerprint size={16} />
          </div>
          <div>
            <div className="text-sm text-[#14100F]">Face ID quick login</div>
            <div className="text-xs text-[#6A5F58]">
              Your device doesn't support biometric passkeys yet. Try on a modern iPhone, iPad, Mac (Touch ID) or Android (Chrome).
            </div>
          </div>
        </div>
      </div>
    );
  }

  const add = async () => {
    setBusy(true);
    try {
      const r = await registerPasskey({ apiBase: API_BASE });
      if (r.cancelled) toast.info("Passkey setup cancelled.");
      else if (r.ok) { toast.success("Face ID enabled on this device."); await refresh(); }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't register the passkey.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this device's Face ID? You'll need to sign in with password next time.")) return;
    try {
      await deletePasskey({ apiBase: API_BASE, credentialId: id });
      toast.success("Removed.");
      await refresh();
    } catch { toast.error("Couldn't remove."); }
  };

  return (
    <div className="bg-white border border-[#DDD1BE] rounded-2xl p-6 space-y-4" data-testid="pwa-passkey-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#0F0E0E] text-[#F5EFE6] flex items-center justify-center">
          <ScanFace size={16} />
        </div>
        <div>
          <div className="text-sm text-[#14100F]">Face ID quick login</div>
          <div className="text-xs text-[#6A5F58]">
            One tap sign-in with Face ID / Touch ID / fingerprint. Passkeys are tied to this device — set one up on every phone or tablet you use.
          </div>
        </div>
      </div>

      {!loading && creds.length > 0 && (
        <ul className="divide-y divide-[#EDE3D2] border border-[#EDE3D2] rounded-lg overflow-hidden" data-testid="pwa-passkey-list">
          {creds.map((c) => (
            <li key={c.credential_id} className="flex items-center justify-between px-4 py-3 bg-[#FAF5EC]">
              <div>
                <div className="text-sm text-[#14100F]">{c.label || "Passkey"}</div>
                <div className="text-xs text-[#6A5F58]">Added {c.created_at ? new Date(c.created_at).toLocaleDateString() : "recently"}</div>
              </div>
              <button
                type="button"
                onClick={() => remove(c.credential_id)}
                aria-label="Remove passkey"
                className="text-[#6A5F58] hover:text-[#B76E79]"
                data-testid={`pwa-passkey-remove-${c.credential_id.slice(0, 8)}`}
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        type="button"
        onClick={add}
        disabled={busy}
        data-testid="pwa-passkey-add"
        className="btn-primary font-brand text-xs tracking-[0.14em] uppercase inline-flex items-center gap-2 disabled:opacity-50"
      >
        <ScanFace size={13} /> {creds.length ? "Add another device" : "Enable Face ID on this device"}
      </button>
    </div>
  );
};

